import React, { useState, useRef, useEffect } from 'react';
import { 
  Monitor, 
  Printer, 
  Laptop, 
  Tv, 
  Gamepad2, 
  Music, 
  Circle, 
  ChevronRight,
  Network
} from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';

interface NodeContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  position: { x: number; y: number };
}

interface NodeType {
  id: string;
  name: string;
  icon: React.ReactNode;
  type: string;
  hasSubmenu?: boolean;
}

const NodeContextMenu: React.FC<NodeContextMenuProps> = ({ isOpen, onClose, position }) => {
  const { setDraggedItem, currentProject } = useProjectStore();
  const [showGenericSubmenu, setShowGenericSubmenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);

  const nodeTypes: NodeType[] = [
    { id: 'computer', name: 'Computer', icon: <Monitor size={16} />, type: 'computer' },
    { id: 'printer', name: 'Printer', icon: <Printer size={16} />, type: 'printer' },
    { id: 'laptop', name: 'Laptop', icon: <Laptop size={16} />, type: 'laptop' },
    { id: 'tv', name: 'TV', icon: <Tv size={16} />, type: 'tv' },
    { id: 'game-console', name: 'Game Console', icon: <Gamepad2 size={16} />, type: 'game-console' },
    { id: 'media-player', name: 'Media Player', icon: <Music size={16} />, type: 'media-player' },
    { 
      id: 'generic-node', 
      name: 'Generic Node', 
      icon: <Circle size={16} />, 
      type: 'generic-node',
      hasSubmenu: true 
    }
  ];

  const portOptions = [
    { ports: 1, label: '1 Port' },
    { ports: 2, label: '2 Ports' },
    { ports: 3, label: '3 Ports' },
    { ports: 4, label: '4 Ports' }
  ];

  // Generate unique node name with auto-numbering
  // This function uses currentProject from closure, which comes from a hook
  // We pass it as a parameter to avoid ESLint warnings
  const generateUniqueNodeName = (baseName: string, project: typeof currentProject): string => {
    if (!project) return baseName;
    
    const existingNames = project.equipment.map(eq => eq.equipment.name);
    
    // Count all equipment that have this base name (including those with auto-numbering)
    const baseNameMatches = project.equipment.filter(eq => {
      const cleanName = eq.equipment.name.replace(/ \(\d+\)$/, '');
      return cleanName === baseName;
    });
    
    const totalCount = baseNameMatches.length;
    
    if (totalCount === 0) {
      return baseName;
    }
    
    // Find the next available number
    let counter = totalCount + 1;
    let uniqueName = `${baseName} (${counter})`;
    
    // Make sure the name doesn't already exist
    while (existingNames.includes(uniqueName)) {
      counter++;
      uniqueName = `${baseName} (${counter})`;
    }
    
    return uniqueName;
  };

  const createNodeEquipment = (type: string, portCount: number = 1) => {
    const baseName = getNodeName(type, portCount);
    const uniqueName = generateUniqueNodeName(baseName, currentProject);
    
    const equipment = {
      id: `node-${Date.now()}`,
      name: uniqueName,
      type: type as any,
      manufacturer: 'generic' as any,
      specifications: {
        ports: portCount
      }
    };

    // Set dragged item for placement (same as Add Equipment modal)
    setDraggedItem({ type: 'equipment', equipment });
    onClose();
  };

  const getNodeName = (type: string, portCount?: number): string => {
    const names: Record<string, string> = {
      'computer': 'Desktop Computer',
      'printer': 'Network Printer',
      'laptop': 'Laptop Computer',
      'tv': 'Smart TV',
      'game-console': 'Gaming Console',
      'media-player': 'Media Player',
      'generic-node': 'Generic Node'
    };
    
    const baseName = names[type] || 'Node';
    
    // For generic nodes, include port count in the name
    if (type === 'generic-node' && portCount) {
      return `${baseName} ${portCount}-Port`;
    }
    
    return baseName;
  };

  const handleNodeClick = (nodeType: NodeType) => {
    if (nodeType.hasSubmenu) {
      setShowGenericSubmenu(true);
    } else {
      // All non-generic nodes have exactly 1 port
      createNodeEquipment(nodeType.type, 1);
    }
  };

  const handlePortOptionClick = (portCount: number) => {
    createNodeEquipment('generic-node', portCount);
    setShowGenericSubmenu(false);
  };

  // Reset submenu state when menu opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowGenericSubmenu(false);
    }
  }, [isOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is outside both main menu and submenu
      const isOutsideMainMenu = menuRef.current && !menuRef.current.contains(target);
      const isOutsideSubmenu = !submenuRef.current || !submenuRef.current.contains(target);
      
      if (isOutsideMainMenu && isOutsideSubmenu) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div
        ref={menuRef}
        className="fixed bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-50 py-2 min-w-[180px]"
        style={{
          left: position.x,
          top: position.y,
        }}
      >
        {nodeTypes.map((nodeType) => (
          <button
            key={nodeType.id}
            onClick={() => handleNodeClick(nodeType)}
            className="w-full px-4 py-2 text-left text-white hover:bg-slate-700 flex items-center space-x-3 transition-colors"
            onMouseEnter={() => {
              if (nodeType.hasSubmenu) {
                setShowGenericSubmenu(true);
              }
            }}
            onMouseLeave={() => {
              if (nodeType.hasSubmenu) {
                setShowGenericSubmenu(false);
              }
            }}
          >
            <span className="text-slate-300">{nodeType.icon}</span>
            <span className="text-sm font-medium">{nodeType.name}</span>
            {nodeType.hasSubmenu && (
              <ChevronRight size={14} className="ml-auto text-slate-400" />
            )}
          </button>
        ))}
      </div>

      {/* Generic Node Submenu */}
      {showGenericSubmenu && (
        <div
          ref={submenuRef}
          className="fixed bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-50 py-2 min-w-[140px]"
          style={{
            left: position.x + 180,
            top: position.y + (nodeTypes.findIndex(n => n.id === 'generic-node') * 40) + 8,
          }}
        >
          {portOptions.map((option) => (
            <button
              key={option.ports}
              onClick={() => handlePortOptionClick(option.ports)}
              className="w-full px-4 py-2 text-left text-white hover:bg-slate-700 flex items-center space-x-3 transition-colors"
            >
              <Network size={16} className="text-slate-300" />
              <span className="text-sm font-medium">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default NodeContextMenu;
